import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Nullable } from '@Types';
import { type LearnerProject } from 'LearnerProjects';
import { Modal } from 'Modal';
import './locales/view_project_group_modal_page-en.json';
import { type ProjectGroupMembershipSansProjectGroup } from './ProjectGroup.types';
import { projectGroupMembershipsApi } from './projectGroupMembershipsApi';
import { ViewProjectGroupModalPage } from './ViewProjectGroupModalPage';

function ViewProjectGroupModalComponent(props: {
    closeModal: () => void;
    isOpen: boolean;
    project: LearnerProject;
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>;
}) {
    const { closeModal, isOpen, project, projectGroupMembership } = props;
    const [isLeaveGroupModalOpen, setIsLeaveGroupModalOpen] = useState(false);
    const { t } = useTranslation('back_royal');
    const title = t('view_project_group_modal.title');
    const [apiError, setApiError] = useState<string | null>(null);
    const [remove, { isLoading: isRemoving }] = projectGroupMembershipsApi.useRemoveMutation();

    if (!isOpen) return null;
    if (!projectGroupMembership) return null;

    const handleLeaveGroup = async () => {
        setApiError(null);

        try {
            const response = await remove({ id: projectGroupMembership.id });
            if (response.error) {
                setApiError(t('view_project_group_modal.api_error'));
                return;
            }
            setIsLeaveGroupModalOpen(false);
            closeModal();
        } catch {
            setApiError(t('view_project_group_modal.api_error'));
        }
    };

    const handleLeaveGroupModalClose = () => {
        setApiError(null);
        setIsLeaveGroupModalOpen(false);
    };

    return (
        <Modal
            classNames={{
                base: 'w-[650px] max-w-[90vw]',
                wrapper: isLeaveGroupModalOpen ? 'z-[9998]' : '',
                header: 'text-center',
                body: 'mx-7.5 mb-6',
            }}
            dismissOnEscape={false}
            dismissOnOverlayClick={false}
            onClose={closeModal}
            scrollBehavior="outside"
            title={title}
        >
            <ViewProjectGroupModalPage
                closeModal={closeModal}
                isOpen={isOpen}
                project={project}
                projectGroupMembership={projectGroupMembership}
                isLeaveGroupModalOpen={isLeaveGroupModalOpen}
                isLeavingGroup={isRemoving}
                apiError={apiError}
                onLeaveGroupModalClose={handleLeaveGroupModalClose}
                onLeaveGroupPress={() => setIsLeaveGroupModalOpen(true)}
                onLeaveGroupConfirmPress={handleLeaveGroup}
            />
        </Modal>
    );
}
export const ViewProjectGroupModal = memo(ViewProjectGroupModalComponent) as typeof ViewProjectGroupModalComponent;
